import { z } from "zod";
import { AI_TOOLKIT_ERROR_CODES } from "./error-codes";

export enum AiToolkitToolName {
  GetNote = "getNote",
  SearchNotes = "searchNotes",
  CreateNote = "createNote",
  UpdateNote = "updateNote",
  GetAssistantTask = "getAssistantTask",
  SearchAssistantTasks = "searchAssistantTasks",
  CreateAssistantTask = "createAssistantTask",
  GetAssistantProject = "getAssistantProject",
  SearchAssistantProjects = "searchAssistantProjects",
  GetAssistantFollowUp = "getAssistantFollowUp",
  SearchAssistantFollowUps = "searchAssistantFollowUps",
  GetCalendarEvent = "getCalendarEvent",
  SearchCalendarEvents = "searchCalendarEvents",
  GetCollection = "getCollection",
  SearchCollections = "searchCollections",
  ListCollectionItems = "listCollectionItems",
  AddNoteToCollection = "addNoteToCollection",
  RemoveNoteFromCollection = "removeNoteFromCollection",
  NavigateToNote = "navigateToNote",
  NavigateToCollection = "navigateToCollection",
}

export const aiToolkitToolNameSchema = z.enum([
  AiToolkitToolName.GetNote,
  AiToolkitToolName.SearchNotes,
  AiToolkitToolName.CreateNote,
  AiToolkitToolName.UpdateNote,
  AiToolkitToolName.GetAssistantTask,
  AiToolkitToolName.SearchAssistantTasks,
  AiToolkitToolName.CreateAssistantTask,
  AiToolkitToolName.GetAssistantProject,
  AiToolkitToolName.SearchAssistantProjects,
  AiToolkitToolName.GetAssistantFollowUp,
  AiToolkitToolName.SearchAssistantFollowUps,
  AiToolkitToolName.GetCalendarEvent,
  AiToolkitToolName.SearchCalendarEvents,
  AiToolkitToolName.GetCollection,
  AiToolkitToolName.SearchCollections,
  AiToolkitToolName.ListCollectionItems,
  AiToolkitToolName.AddNoteToCollection,
  AiToolkitToolName.RemoveNoteFromCollection,
  AiToolkitToolName.NavigateToNote,
  AiToolkitToolName.NavigateToCollection,
]);

export const aiToolkitNoteContentFormatSchema = z.enum(["none", "plaintext", "mdx"]);

export const aiToolkitErrorCodeSchema = z.enum([
  AI_TOOLKIT_ERROR_CODES.UnknownTool,
  AI_TOOLKIT_ERROR_CODES.InvalidInput,
  AI_TOOLKIT_ERROR_CODES.NoteNotFound,
  AI_TOOLKIT_ERROR_CODES.NoteContentNotFound,
  AI_TOOLKIT_ERROR_CODES.AssistantTaskNotFound,
  AI_TOOLKIT_ERROR_CODES.AssistantProjectNotFound,
  AI_TOOLKIT_ERROR_CODES.AssistantFollowUpNotFound,
  AI_TOOLKIT_ERROR_CODES.AssistantNotFound,
  AI_TOOLKIT_ERROR_CODES.CalendarEventNotFound,
  AI_TOOLKIT_ERROR_CODES.CollectionNotFound,
  AI_TOOLKIT_ERROR_CODES.ExecutionFailed,
]);

export const getNoteInputSchema = z
  .object({
    noteId: z.string().min(1).describe("The ID of the local Mem note to retrieve."),
    includeContent: aiToolkitNoteContentFormatSchema
      .optional()
      .describe("Whether to include no content, plaintext content, or MDX content."),
    maxContentChars: z
      .number()
      .int()
      .min(1)
      .max(20_000)
      .optional()
      .describe("Maximum number of note content characters to return."),
  })
  .strict();

export const searchNotesInputSchema = z
  .object({
    query: z
      .string()
      .optional()
      .describe(
        "Optional query matched against the user's note titles and body content via Mem's smart-search engine."
      ),
    limit: z
      .number()
      .int()
      .min(1)
      .max(25)
      .optional()
      .describe("Maximum number of matching notes to return."),
    includeContentPreview: z
      .boolean()
      .optional()
      .describe("Whether to include a plaintext preview for each matching note."),
    maxContentChars: z
      .number()
      .int()
      .min(1)
      .max(2_000)
      .optional()
      .describe("Maximum number of preview content characters to return per note."),
  })
  .strict();

export const createNoteInputSchema = z
  .object({
    title: z.string().min(1).optional().describe("Optional title for the new note."),
    bodyMdx: z
      .string()
      .optional()
      .describe(
        "Optional MDX/Markdown body for the new note. If title is provided, this should be the body below the title."
      ),
    openAfterCreate: z
      .boolean()
      .optional()
      .describe("Whether to navigate the Mem client UI to the new note after creating it."),
  })
  .strict();

export const updateNoteInputSchema = z
  .object({
    noteId: z.string().min(1).describe("The ID of the local Mem note to update."),
    mdx: z
      .string()
      .min(1)
      .describe(
        "The complete replacement note content as MDX/Markdown, including the title heading if desired."
      ),
  })
  .strict();

export const getAssistantTaskInputSchema = z
  .object({
    taskId: z.string().min(1).describe("The ID of the local assistant task to retrieve."),
  })
  .strict();

export const searchAssistantTasksInputSchema = z
  .object({
    query: z
      .string()
      .optional()
      .describe(
        "Optional case-insensitive query matched against local task title and description."
      ),
    assistantId: z.string().min(1).optional().describe("Optional assistant ID filter."),
    projectId: z.string().min(1).optional().describe("Optional assistant project ID filter."),
    status: z.string().min(1).optional().describe("Optional task status filter."),
    limit: z
      .number()
      .int()
      .min(1)
      .max(25)
      .optional()
      .describe("Maximum number of matching assistant tasks to return."),
  })
  .strict();

export const aiToolkitFollowUpSchema = z
  .object({
    content: z.string().min(1).describe("Reminder text for the assistant follow-up."),
    scheduledFor: z.string().datetime().describe("ISO timestamp for when the follow-up is due."),
    triggerType: z
      .enum(["time_exact", "time_window", "next_interaction"])
      .optional()
      .describe("Optional reminder trigger semantics."),
    commitmentKind: z
      .enum(["due", "deadline"])
      .optional()
      .describe("Optional commitment semantics for the follow-up."),
    dueAt: z.string().datetime().optional().describe("Optional due timestamp."),
    deadlineAt: z.string().datetime().optional().describe("Optional deadline timestamp."),
    triggerNotBefore: z
      .string()
      .datetime()
      .optional()
      .describe("Required lower ISO timestamp bound when triggerType is time_window."),
    triggerNotAfter: z
      .string()
      .datetime()
      .optional()
      .describe("Required upper ISO timestamp bound when triggerType is time_window."),
  })
  .strict()
  .superRefine((followUp, ctx) => {
    if (followUp.triggerType !== "time_window") return;

    if (!followUp.triggerNotBefore) {
      ctx.addIssue({
        code: "custom",
        path: ["triggerNotBefore"],
        message: "triggerNotBefore is required when triggerType is time_window.",
      });
    }

    if (!followUp.triggerNotAfter) {
      ctx.addIssue({
        code: "custom",
        path: ["triggerNotAfter"],
        message: "triggerNotAfter is required when triggerType is time_window.",
      });
    }
  });

export const createAssistantTaskInputSchema = z
  .object({
    title: z.string().min(1).describe("Title for the new assistant task."),
    description: z.string().min(1).optional().describe("Optional task description."),
    status: z.string().min(1).optional().describe("Optional assistant task status."),
    dueAt: z.string().datetime().optional().describe("Optional task due timestamp."),
    projectId: z.string().min(1).optional().describe("Optional assistant project ID."),
    followUp: aiToolkitFollowUpSchema
      .optional()
      .describe("Optional follow-up reminder to create with the task."),
  })
  .strict();

export const getAssistantProjectInputSchema = z
  .object({
    projectId: z.string().min(1).describe("The ID of the local assistant project to retrieve."),
  })
  .strict();

export const searchAssistantProjectsInputSchema = z
  .object({
    query: z
      .string()
      .optional()
      .describe(
        "Optional case-insensitive query matched against local project title and description."
      ),
    assistantId: z.string().min(1).optional().describe("Optional assistant ID filter."),
    status: z.string().min(1).optional().describe("Optional project status filter."),
    limit: z
      .number()
      .int()
      .min(1)
      .max(25)
      .optional()
      .describe("Maximum number of matching assistant projects to return."),
  })
  .strict();

export const getAssistantFollowUpInputSchema = z
  .object({
    followUpId: z.string().min(1).describe("The ID of the local assistant follow-up to retrieve."),
  })
  .strict();

export const searchAssistantFollowUpsInputSchema = z
  .object({
    query: z
      .string()
      .optional()
      .describe("Optional case-insensitive query matched against local follow-up content."),
    assistantId: z.string().min(1).optional().describe("Optional assistant ID filter."),
    projectId: z.string().min(1).optional().describe("Optional assistant project ID filter."),
    taskId: z.string().min(1).optional().describe("Optional assistant task ID filter."),
    sourceContextId: z.string().min(1).optional().describe("Optional source context ID filter."),
    status: z.string().min(1).optional().describe("Optional follow-up status filter."),
    scheduledFrom: z
      .string()
      .min(1)
      .optional()
      .describe("Optional inclusive lower bound for scheduled_for timestamps."),
    scheduledTo: z
      .string()
      .min(1)
      .optional()
      .describe("Optional inclusive upper bound for scheduled_for timestamps."),
    limit: z
      .number()
      .int()
      .min(1)
      .max(25)
      .optional()
      .describe("Maximum number of matching assistant follow-ups to return."),
  })
  .strict();

export const getCalendarEventInputSchema = z
  .object({
    calendarEventId: z.string().min(1).describe("The ID of the local calendar event to retrieve."),
  })
  .strict();

export const searchCalendarEventsInputSchema = z
  .object({
    query: z
      .string()
      .optional()
      .describe("Optional case-insensitive query matched against calendar event titles."),
    from: z
      .string()
      .min(1)
      .optional()
      .describe("Optional inclusive ISO timestamp lower bound for event start time."),
    to: z
      .string()
      .min(1)
      .optional()
      .describe("Optional inclusive ISO timestamp upper bound for event start time."),
    includeHidden: z.boolean().optional().describe("Whether to include hidden calendar events."),
    limit: z
      .number()
      .int()
      .min(1)
      .max(25)
      .optional()
      .describe("Maximum number of matching calendar events to return."),
  })
  .strict();

export const getCollectionInputSchema = z
  .object({
    collectionId: z.string().min(1).describe("The ID of the local collection to retrieve."),
  })
  .strict();

export const searchCollectionsInputSchema = z
  .object({
    query: z
      .string()
      .optional()
      .describe("Optional case-insensitive query matched against local collection titles."),
    isShared: z.boolean().optional().describe("Optional filter for shared collections."),
    isOwnedByMe: z.boolean().optional().describe("Optional filter for collections owned by me."),
    limit: z
      .number()
      .int()
      .min(1)
      .max(25)
      .optional()
      .describe("Maximum number of matching collections to return."),
  })
  .strict();

export const listCollectionItemsInputSchema = z
  .object({
    collectionId: z.string().min(1).describe("The ID of the local collection to list."),
    limit: z
      .number()
      .int()
      .min(1)
      .max(100)
      .optional()
      .describe("Maximum number of collection items to return."),
    includeNotePreview: z
      .boolean()
      .optional()
      .describe("Whether to include a plaintext preview for note collection items."),
    maxContentChars: z
      .number()
      .int()
      .min(1)
      .max(2_000)
      .optional()
      .describe("Maximum number of preview content characters to return per note item."),
  })
  .strict();

export const addNoteToCollectionInputSchema = z
  .object({
    noteId: z.string().min(1).describe("The ID of the local Mem note to add."),
    collectionId: z
      .string()
      .min(1)
      .describe("The ID of the local Mem collection to add the note to."),
  })
  .strict();

export const removeNoteFromCollectionInputSchema = z
  .object({
    noteId: z.string().min(1).describe("The ID of the local Mem note to remove."),
    collectionId: z
      .string()
      .min(1)
      .describe("The ID of the local Mem collection to remove the note from."),
  })
  .strict();

export const navigateToNoteInputSchema = z
  .object({
    noteId: z.string().min(1).describe("The ID of the local Mem note to navigate to."),
    highlightText: z
      .string()
      .min(1)
      .optional()
      .describe("Optional text to highlight after opening the note."),
    openInSameTab: z
      .boolean()
      .optional()
      .describe("Whether to open the note in the current active tab. Defaults to true."),
  })
  .strict();

export const navigateToCollectionInputSchema = z
  .object({
    collectionId: z.string().min(1).describe("The ID of the local Mem collection to navigate to."),
    openInSameTab: z
      .boolean()
      .optional()
      .describe("Whether to open the collection in the current active tab. Defaults to true."),
  })
  .strict();

export const aiToolkitLocalNoteContentSchema = z.discriminatedUnion("status", [
  z
    .object({
      status: z.literal("not_requested"),
    })
    .strict(),
  z
    .object({
      status: z.literal("not_found"),
    })
    .strict(),
  z
    .object({
      status: z.literal("local"),
      plaintext: z.string().optional(),
      mdx: z.string().optional(),
    })
    .strict(),
]);

export const aiToolkitLocalNoteSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    secondaryTitle: z.string(),
    path: z.string(),
    createdAt: z.string(),
    modifiedAt: z.string(),
    locallyCreatedAt: z.string(),
    isOwnedByMe: z.boolean(),
    isShared: z.boolean(),
    content: aiToolkitLocalNoteContentSchema,
  })
  .strict();

export const aiToolkitAssistantTaskSchema = z
  .object({
    id: z.string(),
    assistantId: z.string(),
    projectId: z.string().nullable(),
    title: z.string(),
    description: z.string().nullable(),
    status: z.string(),
    dueAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    pending: z
      .boolean()
      .optional()
      .describe("True when this is a local projection for a pending create request."),
  })
  .strict();

export const aiToolkitAssistantProjectSchema = z
  .object({
    id: z.string(),
    assistantId: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    status: z.string(),
    lastActivityAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    pending: z
      .boolean()
      .optional()
      .describe("True when this is a local projection for a pending create request."),
  })
  .strict();

export const aiToolkitAssistantFollowUpSchema = z
  .object({
    id: z.string(),
    assistantId: z.string(),
    projectId: z.string().nullable(),
    taskId: z.string().nullable(),
    sourceContextId: z.string().nullable(),
    content: z.string(),
    status: z.string(),
    scheduledFor: z.string(),
    firedAt: z.string().nullable(),
    completedAt: z.string().nullable(),
    commitmentKind: z.string().nullable(),
    dueAt: z.string().nullable(),
    deadlineAt: z.string().nullable(),
    triggerType: z.string().nullable(),
    triggerNotBefore: z.string().nullable(),
    triggerNotAfter: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    pending: z
      .boolean()
      .optional()
      .describe("True when this is a local projection for a pending create request."),
  })
  .strict();

export const aiToolkitCalendarEventAssociatedNoteSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    path: z.string(),
  })
  .strict();

export const aiToolkitCalendarEventSchema = z
  .object({
    id: z.string(),
    associatedConnectedCalendarEventId: z.string(),
    title: z.string().nullable(),
    startTime: z.string().nullable(),
    endTime: z.string().nullable(),
    isAllDay: z.boolean(),
    isHidden: z.boolean(),
    attendeeCount: z.number().nullable(),
    selfResponseStatus: z.string().nullable(),
    meetingUrl: z.string().nullable(),
    meetingPlatform: z.string(),
    calendarName: z.string().nullable(),
    calendarEmailAddress: z.string().nullable(),
    associatedNote: aiToolkitCalendarEventAssociatedNoteSchema.nullable(),
  })
  .strict();

export const aiToolkitCollectionSchema = z
  .object({
    id: z.string(),
    title: z.string().nullable(),
    label: z.string(),
    description: z.string().nullable(),
    path: z.string(),
    isShared: z.boolean(),
    isFavorited: z.boolean(),
    itemCount: z.number().int().nonnegative(),
    createdAt: z.string(),
    modifiedAt: z.string(),
    lastViewedAt: z.string().nullable(),
    lastAddedToAt: z.string().nullable(),
    lastInteractedAt: z.string().nullable(),
    sharedAt: z.string().nullable(),
  })
  .strict();

export const aiToolkitCollectionItemSchema = z
  .object({
    id: z.string(),
    collectionId: z.string(),
    itemId: z.string(),
    itemKind: z.string(),
    addedAt: z.string(),
    addedBySpaceAccountId: z.string().nullable(),
    note: aiToolkitLocalNoteSchema.nullable(),
  })
  .strict();

export const getNoteOutputSchema = z
  .object({
    note: aiToolkitLocalNoteSchema,
  })
  .strict();

export const searchNotesOutputSchema = z
  .object({
    notes: z.array(aiToolkitLocalNoteSchema),
  })
  .strict();

export const createNoteOutputSchema = z
  .object({
    created: z.literal(true),
    noteId: z.string(),
  })
  .strict();

export const updateNoteOutputSchema = z
  .object({
    updated: z.literal(true),
    noteId: z.string(),
  })
  .strict();

export const getAssistantTaskOutputSchema = z
  .object({
    task: aiToolkitAssistantTaskSchema,
  })
  .strict();

export const searchAssistantTasksOutputSchema = z
  .object({
    tasks: z.array(aiToolkitAssistantTaskSchema),
  })
  .strict();

export const createAssistantTaskOutputSchema = z
  .object({
    submitted: z.literal(true),
    taskId: z.string(),
    followUpId: z.string().optional(),
    requestGroupId: z.string().optional(),
  })
  .strict();

export const getAssistantProjectOutputSchema = z
  .object({
    project: aiToolkitAssistantProjectSchema,
  })
  .strict();

export const searchAssistantProjectsOutputSchema = z
  .object({
    projects: z.array(aiToolkitAssistantProjectSchema),
  })
  .strict();

export const getAssistantFollowUpOutputSchema = z
  .object({
    followUp: aiToolkitAssistantFollowUpSchema,
  })
  .strict();

export const searchAssistantFollowUpsOutputSchema = z
  .object({
    followUps: z.array(aiToolkitAssistantFollowUpSchema),
  })
  .strict();

export const getCalendarEventOutputSchema = z
  .object({
    calendarEvent: aiToolkitCalendarEventSchema,
  })
  .strict();

export const searchCalendarEventsOutputSchema = z
  .object({
    calendarEvents: z.array(aiToolkitCalendarEventSchema),
  })
  .strict();

export const getCollectionOutputSchema = z
  .object({
    collection: aiToolkitCollectionSchema,
  })
  .strict();

export const searchCollectionsOutputSchema = z
  .object({
    collections: z.array(aiToolkitCollectionSchema),
  })
  .strict();

export const listCollectionItemsOutputSchema = z
  .object({
    items: z.array(aiToolkitCollectionItemSchema),
  })
  .strict();

export const addNoteToCollectionOutputSchema = z
  .object({
    noteId: z.string(),
    collectionId: z.string(),
    added: z.boolean(),
    alreadyInCollection: z.boolean(),
  })
  .strict();

export const removeNoteFromCollectionOutputSchema = z
  .object({
    noteId: z.string(),
    collectionId: z.string(),
    removed: z.boolean(),
  })
  .strict();

export const navigateToNoteOutputSchema = z
  .object({
    navigated: z.literal(true),
    noteId: z.string(),
  })
  .strict();

export const navigateToCollectionOutputSchema = z
  .object({
    navigated: z.literal(true),
    collectionId: z.string(),
  })
  .strict();

export const aiToolkitSerializedErrorSchema = z
  .object({
    code: aiToolkitErrorCodeSchema,
    message: z.string(),
    name: z.string(),
    info: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();
